'use client'

import { useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { faArrowRight, faRectangleHistory } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from '@teqplay/ui'

import ModalESOF from '../../Modals/ModalESOF'

import type { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import useSof from '@/app/hooks/teqplay-api/useSof'
import DateClient from '@/app/components/ClientDateFormat/ClientDateFormat'

import './StatementOfFacts.scss'

type TProps = {
  vessel: TVesselJourneyResponse
}

export default function StatementOfFacts({ vessel }: TProps) {
  const t = useTranslations('StatementOfFacts')
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const params = useMemo(() => {
    return {
      imo: vessel.ship.imo,
      unlocode: vessel.arrival?.port?.unlocode,
      portId: vessel.arrival?.port?.id
    }
  }, [vessel.ship.imo, vessel.arrival?.port?.unlocode, vessel.arrival?.port?.id])

  const { data, isLoading, error } = useSof(params)

  const events = useMemo(() => {
    const list = data ?? []
    return [...list].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
  }, [data])

  if (events.length === 0) return null

  return (
    <section className="vessel-sof">
      <div className="statement-of-facts">
        <div className="title">
          <h3>
            <FontAwesomeIcon icon={faRectangleHistory} />
            <span>{t('title')}</span>
          </h3>
          <Button variant="brand-primary" onClick={() => setIsModalOpen(true)}>
            <div className="flex items-center gap-2xs">
              <span>{t('discoverESOF')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </div>
          </Button>

          <ModalESOF isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>

        {isLoading && <div className="loading">{t('loading')}</div>}
        {!!error && !isLoading && <div className="error">{t('error')}</div>}

        {!isLoading && !error && (
          <div className="events">
            {events.length === 0 ? (
              <div className="empty">{t('empty')}</div>
            ) : (
              events.map((e, idx) => (
                <div className="event-item" key={`${e.eventType}-${e.timestamp}-${idx}`}>
                  <div className="event-content">
                    <div className="event-title">
                      {t(`events.${e.eventType}` as unknown as Parameters<typeof t>[0], {
                        portName: e.portName ?? '',
                        locationName: e.locationName ?? '',
                        otherShipName: e.otherShipName ?? ''
                      })}
                    </div>
                  </div>
                  <div className="event-timestamp">
                    <DateClient date={e.timestamp} />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </section>
  )
}
